/* global fetch */

import { config } from '../config';
import { APIResponse, fetchAPI, headers, SpanTag, stleaf } from '../helpers';

export interface StacAsset {
  href: string;
  type?: string;
  title?: string;
  roles?: string[];
}

export interface StacItem {
  id: string;
  collection: string;
  type: string;
  stacVersion: string;
  geometry: any;
  bbox: number[];
  properties: Record<string, any>;
  assets: Record<string, StacAsset>;
  links: any[];
  createdAt: string;
  updatedAt: string;
}

export interface StacCollection {
  id: string;
  type: string;
  stacVersion: string;
  title: string;
  description: string;
  license: string | null;
  extent: Record<string, any>;
  keywords: string[] | null;
  providers: Record<string, any> | null;
  summaries: Record<string, any> | null;
  links: any[];
  createdAt: string;
  updatedAt: string;
}

export interface StacItemsResponse {
  type: 'FeatureCollection';
  features: StacItem[];
  links: any[];
}

export interface StacCollectionsResponse {
  collections: StacCollection[];
  links: any[];
}

export interface GetStacItemsOptions {
  bbox?: string; // "minX,minY,maxX,maxY"
  collection?: string;
  asset_type?: string;
}

export interface GetStacCollectionsOptions {
  asset_type?: string;
}

/**
 * Get all STAC items with optional filters
 */
type GetStacItemsResponse = Promise<APIResponse<StacItemsResponse>>;
export const getItems = (
  st: SpanTag,
  opts?: GetStacItemsOptions,
): GetStacItemsResponse => {
  const url = new URL(`${config.API_URL}/v1/stac/items`);
  if (opts) url.search = new URLSearchParams(opts as any).toString();
  return fetchAPI(stleaf(st, 'stacitms'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  }) as GetStacItemsResponse;
};

/**
 * Get a single STAC item by ID
 */
type GetStacItemResponse = Promise<APIResponse<StacItem>>;
export const getItemById = (
  st: SpanTag,
  itemId: string,
): GetStacItemResponse => {
  const url = `${config.API_URL}/v1/stac/items/${itemId}`;
  return fetchAPI(stleaf(st, 'stacitm'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  }) as GetStacItemResponse;
};

/**
 * Get all STAC collections with optional filters
 */
type GetStacCollectionsResponse = Promise<APIResponse<StacCollectionsResponse>>;
export const getCollections = (
  st: SpanTag,
  opts?: GetStacCollectionsOptions,
): GetStacCollectionsResponse => {
  const url = new URL(`${config.API_URL}/v1/stac/collections`);
  if (opts) url.search = new URLSearchParams(opts as any).toString();
  return fetchAPI(stleaf(st, 'staccols'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  }) as GetStacCollectionsResponse;
};

/**
 * Get a single STAC collection by ID
 */
type GetStacCollectionResponse = Promise<APIResponse<StacCollection>>;
export const getCollectionById = (
  st: SpanTag,
  collectionId: string,
): GetStacCollectionResponse => {
  const url = `${config.API_URL}/v1/stac/collections/${collectionId}`;
  return fetchAPI(stleaf(st, 'staccol'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  }) as GetStacCollectionResponse;
};

/**
 * Get all items in a specific collection
 */
type GetCollectionItemsResponse = Promise<APIResponse<StacItemsResponse>>;
export const getCollectionItems = (
  st: SpanTag,
  collectionId: string,
): GetCollectionItemsResponse => {
  const url = `${config.API_URL}/v1/stac/collections/${collectionId}/items`;
  return fetchAPI(stleaf(st, 'staccolitms'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  }) as GetCollectionItemsResponse;
};
